import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/WalletPage.css';
import { useAuth } from '../context/AuthContext';
import { signOutUser, getUserProfile, getUserIconPath } from '../services/userService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import Footer from '../components/Footer';
import { Prize } from '../types/prize';

const WalletPage: React.FC = () => {
  const [activePrizes, setActivePrizes] = useState<Prize[]>([]);
  const [sharedPrizes, setSharedPrizes] = useState<Prize[]>([]);
  const [redeemedPrizes, setRedeemedPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userName, setUserName] = useState<string>('');
  const [userIconPath, setUserIconPath] = useState<string>('/images/User_icon.png');

  useEffect(() => {
    // Get the user's first name and icon
    if (currentUser && currentUser.displayName) {
      const firstName = currentUser.displayName.split(' ')[0];
      setUserName(firstName);
    }

    // Fetch user profile for icon
    if (currentUser) {
      const fetchUserIcon = async () => {
        try {
          const profile = await getUserProfile(currentUser.uid);
          if (profile) {
            setUserIconPath(getUserIconPath(profile.userIcon));
          }
        } catch (error) {
          console.error('Error fetching user icon:', error);
        }
      };

      fetchUserIcon();
    }

    // Fetch prizes from Firestore
    if (!currentUser) return;
    const fetchPrizes = async () => {
      try {
        setLoading(true);
        const prizesRef = collection(db, 'prizes');
        const q = query(prizesRef, where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        const fetchedPrizes: Prize[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedPrizes.push({
            id: doc.id,
            userId: data.userId,
            campaignId: data.campaignId,
            brandName: data.brandName,
            brandLogo: data.brandLogo,
            prizeTitle: data.prizeTitle,
            prizeValue: data.prizeValue,
            category: data.category,
            claimedAt: data.claimedAt?.toDate ? data.claimedAt.toDate() : data.claimedAt,
            expiresAt: data.expiresAt?.toDate ? data.expiresAt.toDate() : data.expiresAt,
            isRedeemed: data.isRedeemed || false,
            redeemedAt: data.redeemedAt?.toDate ? data.redeemedAt.toDate() : data.redeemedAt,
            qrCode: data.qrCode,
            beenShared: data.beenShared || false,
            sharedTo: data.sharedTo || [],
            sharedToUsernames: data.sharedToUsernames || [],
            senderId: data.senderId,
            originalWinnerId: data.originalWinnerId,
            sharedAt: data.sharedAt?.toDate ? data.sharedAt.toDate() : data.sharedAt,
            giftMessage: data.giftMessage
          });
        });
        
        // Separate active, shared, and redeemed prizes
        const active = fetchedPrizes.filter(prize => !prize.isRedeemed && !prize.beenShared);
        const shared = fetchedPrizes.filter(prize => prize.beenShared);
        const redeemed = fetchedPrizes
          .filter(prize => prize.isRedeemed)
          .sort((a, b) => {
            // Sort by redeemedAt, newest first
            if (!a.redeemedAt) return 1;
            if (!b.redeemedAt) return -1;
            return b.redeemedAt.getTime() - a.redeemedAt.getTime();
          });
        
        setActivePrizes(active);
        setSharedPrizes(shared);
        setRedeemedPrizes(redeemed);
      } catch (error) {
        console.error('Error fetching prizes:', error);
        setActivePrizes([]);
        setSharedPrizes([]);
        setRedeemedPrizes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPrizes();
  }, [currentUser, navigate]);

  const handleGoToMain = () => {
    navigate('/main');
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate('/'); // Redirect to LoaderPage
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleLogoClick = () => {
    if (currentUser) {
      navigate('/main');
    }
  };

  return (
    <div className="wallet-page fade-in">
      {/* Header */}
      <div className="main-header">
        <div className="main-logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src="/images/logo.png" alt="Wiin Logo" className="main-logo" />
        </div>
        <div className="main-profile-info">
          <div className="main-greeting-container">
            <h2 className="main-greeting">Hej {userName}!</h2>
            <img 
              src={userIconPath} 
              alt="User Avatar" 
              className="main-avatar"
            />
          </div>
          <p className="profile-logout-link" onClick={handleLogout}>Log ud</p>
        </div>
      </div>

      {/* Active Prizes Section */}
      <div className="prizes-section">
        <h2>Dine præmier</h2>
        {loading ? (
          <div className="loading-prizes">
            <div className="spinner"></div>
            <p>Indlæser dine præmier...</p>
          </div>
        ) : activePrizes.length > 0 ? (
          <div className="prize-list">
            {activePrizes.map((prize) => (
              <div 
                key={prize.id} 
                className="prize-container"
                onClick={() => navigate(`/prize/${prize.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src={prize.brandLogo}
                  alt={`${prize.brandName} logo`}
                  className="sponsor-logo"
                />
                <div className="prize-content">
                  <p className="prize-countdown-header">Din præmie udløber om:</p>
                  <p className="prize-countdown-time">
                    2 dage, 6 timer, 42 min og 43 sek.
                  </p>
                  
                  <div className="prize-header">
                    <h3>{prize.brandName}</h3>
                  </div>
                  
                  <p>{prize.prizeTitle}</p>
                  <p className="prize-value-text">{prize.prizeValue}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-prizes">
            <p>Du har ingen aktive præmier</p>
            <button className="main-cta-button" onClick={handleGoToMain}>
              <span>Spil og vind</span>
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.04 9.07L22.04 13.35L17.04 17.63"
                  stroke="#FFFFFF"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2.09 13.35H21.67"
                  stroke="#FFFFFF"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Shared Prizes Section */}
      {sharedPrizes.length > 0 && (
        <div className="prizes-section shared-section">
          <h2>Sendt til venner</h2>
          <div className="prize-list">
            {sharedPrizes.map((prize) => (
              <div 
                key={prize.id} 
                className="prize-container shared-prize"
              >
                <div className="shared-icon-container">
                  <img 
                    src="/images/sentto.png"
                    alt="Sendt til"
                    className="shared-icon"
                  />
                </div>
                <img 
                  src={prize.brandLogo}
                  alt={`${prize.brandName} logo`}
                  className="sponsor-logo"
                />
                <div className="prize-content">
                  <div className="prize-header">
                    <h3>{prize.brandName}</h3>
                  </div>
                  
                  <p>{prize.prizeTitle}</p>
                  <p className="prize-value-text">{prize.prizeValue}</p>
                  
                  <p className="shared-to-text">
                    Sendt til: {prize.sharedToUsernames.join(', ')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Redeemed Prizes Section */}
      {redeemedPrizes.length > 0 && (
        <div className="prizes-section redeemed-section">
          <h2>Brugte præmier</h2>
          <div className="prize-list">
            {redeemedPrizes.map((prize) => (
              <div 
                key={prize.id} 
                className="prize-container redeemed-prize"
              >
                <img 
                  src={prize.brandLogo}
                  alt={`${prize.brandName} logo`}
                  className="sponsor-logo"
                />
                <div className="prize-content">
                  <div className="prize-header">
                    <h3>{prize.brandName}</h3>
                  </div>
                  
                  <p>{prize.prizeTitle}</p>
                  <p className="prize-value-text">{prize.prizeValue}</p>
                </div>
                <img 
                  src="/images/redeemed.png" 
                  alt="Redeemed" 
                  className="redeemed-checkmark"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer Navigation */}
      <Footer />
    </div>
  );
};

export default WalletPage;
